import axios from "./axios";

/** Canonical work order edit URL (matches `dashboard.ts` route). */
export const workOrderEditPath = (workOrderId) =>
  workOrderId ? `/dashboard/work_orders/edit/${workOrderId}` : null;

export const workOrderReadPath = (workOrderId) =>
  workOrderId ? `/dashboard/work_orders/${workOrderId}` : null;

export const fetchProjectEvents = (params) =>
  axios.get("/project_events", { params });

export const createProjectEvent = (project_event, fields = "*") =>
  axios.post("/project_events", { project_event, fields });

/** True when the plan belongs in the unscheduled sidebar (no crew rows yet). */
export const isDraftPlan = (event) => {
  if (!event) return false;
  if (event.status === "scheduled") return false;
  if (event.status === "draft") return true;
  return !(event.resources || []).length;
};

/** One canonical plan per work order: scheduled first, then draft, then newest. */
export const pickPrimaryPlanForWorkOrder = (plans = []) => {
  if (!plans?.length) return null;
  const rank = (p) => {
    if (p.status === "scheduled") return 0;
    if (p.status === "draft") return 1;
    return 2;
  };
  return [...plans].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return (b.id || 0) - (a.id || 0);
  })[0];
};

export const fetchWorkOrderProjectEvents = (workOrderId, params = {}) =>
  fetchProjectEvents({
    work_order_id: workOrderId,
    per_page: params.per_page ?? 50,
    page: params.page ?? 1,
    sortBy: params.sortBy ?? "updated_at",
    sort: params.sort ?? "DESC",
    fields:
      params.fields ??
      "id,title,description,status,start_date,end_date,project_id,work_order_id,project_activity_id,links,project",
    ...params,
  });

export const fetchProjectEventConflicts = (params) =>
  axios.get("/project_events/conflicts", { params });

export const updateProjectEvent = (id, project_event) =>
  axios.patch(`/project_events/${id}`, { project_event });

export const updateProjectEventResource = (id, project_event_resource) =>
  axios.patch(`/project_event_resources/${id}`, { project_event_resource });

export const completeProjectEventResource = (id) =>
  axios.post(`/project_event_resources/${id}/complete`);

export const plannedHoursBetween = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const start = new Date(startTime);
  const end = new Date(endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round(((end - start) / 3600000) * 100) / 100);
};

const pad2 = (n) => String(n).padStart(2, "0");

/** Format ISO or datetime-local for `<input type="datetime-local" />`. */
export const formatLocalDateTimeInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && value.length <= 16 && !value.includes("Z")) {
    return value.substring(0, 16);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Hours between two datetime-local values (positive only). */
export const hoursBetweenLocalDateTimes = (startLocal, endLocal) => {
  if (!startLocal || !endLocal) return null;
  const start = new Date(startLocal);
  const end = new Date(endLocal);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const hours = (end - start) / 3600000;
  return hours > 0 ? Math.round(hours * 100) / 100 : null;
};

/** Add decimal hours to a datetime-local start; returns new datetime-local end. */
export const addHoursToLocalDateTime = (startLocal, hours) => {
  if (!startLocal || hours == null || Number.isNaN(Number(hours))) return "";
  const start = new Date(startLocal);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start.getTime() + Number(hours) * 3600000);
  return formatLocalDateTimeInput(end);
};

/** Build datetime-local value from date + hour (0–24 decimal). */
export const dateHourToLocalInput = (dateStr, hourDecimal) => {
  if (!dateStr) return "";
  const hours = Math.floor(hourDecimal);
  const minutes = Math.round((hourDecimal - hours) * 60);
  return `${dateStr}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/** Parse datetime-local / ISO to decimal hour on given day (relative to dayStart). */
export const localInputToDayHour = (value, dayStart = 0) => {
  if (!value) return dayStart;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return dayStart;
  return d.getHours() + d.getMinutes() / 60;
};

export const defaultResourceTimes = (dateStr, startHour = 8, endHour = 16) => ({
  start_time: dateHourToLocalInput(dateStr, startHour),
  end_time: dateHourToLocalInput(dateStr, endHour),
});

export const parseEventLinks = (event) => {
  const workOrderLinks = [];
  const activityLinks = [];
  const seenWo = new Set();
  const seenPa = new Set();

  const pushWo = (wo, isPrimary = false) => {
    if (!wo?.id || seenWo.has(wo.id)) return;
    seenWo.add(wo.id);
    workOrderLinks.push({ record: wo, isPrimary: Boolean(isPrimary) });
  };

  const pushPa = (pa, isPrimary = false) => {
    if (!pa?.id || seenPa.has(pa.id)) return;
    seenPa.add(pa.id);
    activityLinks.push({ record: pa, isPrimary: Boolean(isPrimary) });
  };

  (event?.links || []).forEach((link) => {
    if (link.link_type === "work_order") {
      pushWo(
        link.work_order || { id: link.link_id, displayTitle: `#${link.link_id}` },
        link.is_primary
      );
    }
    if (link.link_type === "project_activity") {
      pushPa(
        link.project_activity || { id: link.link_id, display_title: `#${link.link_id}` },
        link.is_primary
      );
    }
  });

  if (event?.work_order?.id) {
    const existing = workOrderLinks.find((l) => l.record.id === event.work_order.id);
    if (existing) {
      if (event.work_order_id === event.work_order.id) existing.isPrimary = true;
    } else {
      pushWo(event.work_order, event.work_order_id === event.work_order.id);
    }
  }
  if (event?.project_activity?.id) {
    const existing = activityLinks.find((l) => l.record.id === event.project_activity.id);
    if (existing) {
      if (event.project_activity_id === event.project_activity.id) existing.isPrimary = true;
    } else {
      pushPa(event.project_activity, event.project_activity_id === event.project_activity.id);
    }
  }

  ensurePrimaryFlag(workOrderLinks);
  ensurePrimaryFlag(activityLinks);

  return {
    workOrderLinks,
    activityLinks,
    workOrders: workOrderLinks.map((l) => l.record),
    activities: activityLinks.map((l) => l.record),
  };
};

const ensurePrimaryFlag = (links) => {
  if (links.length === 0) return;
  if (!links.some((l) => l.isPrimary)) {
    links[0].isPrimary = true;
  }
};

export const buildLinksPayload = (workOrderLinks = [], activityLinks = []) => {
  const links = [];
  const woList = workOrderLinks.length && workOrderLinks[0]?.record
    ? workOrderLinks
    : (workOrderLinks || []).map((wo) => ({ record: wo, isPrimary: false }));
  const paList = activityLinks.length && activityLinks[0]?.record
    ? activityLinks
    : (activityLinks || []).map((pa) => ({ record: pa, isPrimary: false }));

  ensurePrimaryFlag(woList);
  ensurePrimaryFlag(paList);

  woList.forEach(({ record: wo, isPrimary }) => {
    if (wo?.id) {
      links.push({ link_type: "work_order", link_id: wo.id, is_primary: Boolean(isPrimary) });
    }
  });
  paList.forEach(({ record: pa, isPrimary }) => {
    if (pa?.id) {
      links.push({ link_type: "project_activity", link_id: pa.id, is_primary: Boolean(isPrimary) });
    }
  });
  return links;
};

export const addWorkOrderLink = (links, wo) => {
  if (!wo?.id || links.some((l) => l.record.id === wo.id)) return links;
  const isPrimary = links.length === 0;
  return [...links, { record: wo, isPrimary }];
};

export const addWorkOrderLinks = (links, workOrders = []) =>
  (workOrders || []).reduce((acc, wo) => addWorkOrderLink(acc, wo), links);

export const addActivityLink = (links, pa) => {
  if (!pa?.id || links.some((l) => l.record.id === pa.id)) return links;
  const isPrimary = links.length === 0;
  return [...links, { record: pa, isPrimary }];
};

export const addActivityLinks = (links, activities = []) =>
  (activities || []).reduce((acc, pa) => addActivityLink(acc, pa), links);

export const removeLink = (links, id) => {
  const next = links.filter((l) => l.record.id !== id);
  ensurePrimaryFlag(next);
  return next;
};

export const setPrimaryLink = (links, id) =>
  links.map((l) => ({ ...l, isPrimary: l.record.id === id }));

/** Append work order description block into plan rich-text HTML. */
export const appendWorkOrderDescription = (currentHtml, workOrder) => {
  if (!workOrder?.description) return currentHtml || "";
  const title = workOrder.displayTitle || workOrder.title || `#${workOrder.id}`;
  const block = `<p><strong>${title}</strong></p>${workOrder.description}`;
  if (!currentHtml || currentHtml === "<p><br></p>") return block;
  return `${currentHtml}<p><br></p><hr><p><br></p>${block}`;
};

export const overlapsRange = (aStart, aEnd, bStart, bEnd) => {
  const as = new Date(aStart).getTime();
  const ae = new Date(aEnd).getTime();
  const bs = new Date(bStart).getTime();
  const be = new Date(bEnd).getTime();
  if ([as, ae, bs, be].some(Number.isNaN)) return false;
  return as < be && ae > bs;
};

/** Aggregate scheduled + draft plan stats per project_id. */
export const buildProjectPlanStats = (scheduledEvents = [], draftEvents = []) => {
  const stats = {};

  const ensure = (projectId) => {
    const key = String(projectId);
    if (!stats[key]) {
      stats[key] = {
        scheduledCount: 0,
        draftCount: 0,
        resourceIds: new Set(),
      };
    }
    return stats[key];
  };

  scheduledEvents.forEach((event) => {
    if (!event?.project_id) return;
    const row = ensure(event.project_id);
    row.scheduledCount += 1;
    (event.resources || []).forEach((resource) => {
      if (resource?.user_id) row.resourceIds.add(resource.user_id);
    });
  });

  draftEvents.forEach((event) => {
    if (!event?.project_id) return;
    ensure(event.project_id).draftCount += 1;
  });

  return Object.fromEntries(
    Object.entries(stats).map(([id, row]) => [
      id,
      {
        scheduledCount: row.scheduledCount,
        draftCount: row.draftCount,
        resourceCount: row.resourceIds.size,
        isPlanned: row.scheduledCount > 0 || row.draftCount > 0,
      },
    ])
  );
};

export const filterEventsByUserIds = (events, userIds) => {
  const scheduled = (events || []).filter((event) => !isDraftPlan(event));
  if (!userIds?.length) return scheduled;
  const idSet = new Set(userIds.map(String));
  return scheduled
    .map((event) => ({
      ...event,
      resources: (event.resources || []).filter((r) => idSet.has(String(r.user_id))),
    }))
    .filter((event) => (event.resources || []).length > 0);
};
